import PropTypes from "prop-types";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  LabelList,
  ResponsiveContainer,
  Text,
} from "recharts";
import "./common.css";

const Y_WIDTH = 115;

const CustomTooltip = ({ active, payload }) => {
  if (active && payload?.length) {
    const { name, value, percentage } = payload[0].payload;
    return (
      <div className="custom-tooltip">
        <div className="tooltip-row">
          <div className="tooltip-left">
            <span className="tooltip-label">{name}</span>
          </div>
          <span className="tooltip-value">{`${value} (${percentage}%)`}</span>
        </div>
      </div>
    );
  }
  return null;
};

/** YAxis category tick for the horizontal layout. */
const CategoryTick = ({ y, payload }) => (
  <Text
    x={0}
    y={y}
    width={Y_WIDTH - 4}
    textAnchor="start"
    verticalAnchor="middle"
    fill="#667F93"
    fontSize={12}
  >
    {payload.value}
  </Text>
);

/** Bar label for the horizontal layout: sits inside the bar when it fits. */
const HorizontalBarLabel = ({ x, y, width, height, index, formattedData, dataKey }) => {
  const item = formattedData[index];
  if (!item) return null;
  const label = `${item[dataKey]} (${item.percentage}%)`;
  const approxTextWidth = label.length * 7;
  const fitsInside = width > approxTextWidth + 20;
  return (
    <text
      x={fitsInside ? x + width - 10 : x + width + 8}
      y={y + height / 2 + 5}
      textAnchor={fitsInside ? "end" : "start"}
      fill={fitsInside ? "#ffffff" : "#1A2025"}
      fontSize={12}
      fontWeight={600}
    >
      {label}
    </text>
  );
};

/** XAxis category tick for the vertical layout: wraps onto a second line. */
const WrappedCategoryTick = ({ x, y, payload }) => {
  const words = payload.value.split(" ");
  let firstLine = "";
  let secondLine = "";
  if (words.length <= 2) {
    firstLine = words[0];
    secondLine = words[1] || "";
  } else {
    firstLine = words.slice(0, -1).join(" ");
    secondLine = words[words.length - 1];
  }
  return (
    <text x={x} y={y + 10} textAnchor="middle" fill="#667F93" fontSize={13}>
      <tspan x={x} dy="0">
        {firstLine}
      </tspan>
      {secondLine && (
        <tspan x={x} dy="15">
          {secondLine}
        </tspan>
      )}
    </text>
  );
};

/** Bar label for the vertical layout: value above the bar, percentage beneath it. */
const VerticalBarLabel = ({ x, y, width, value, index, formattedData }) => {
  const percentage = formattedData[index].percentage;
  return (
    <text
      x={x + width / 2}
      y={y - 20}
      fill="#000"
      textAnchor="middle"
      fontSize="13"
      fontWeight="600"
    >
      {value}
      <tspan x={x + width / 2} dy="15" fontSize="12" fontWeight="500">
        ({percentage}%)
      </tspan>
    </text>
  );
};

const CommonBarGraph = ({
  data,
  dataKey = "value",
  nameKey = "name",
  color = "#2f3e9e",
  horizontal = false,
}) => {
  if (!data || data.length === 0) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", minHeight: 200, color: "#667F93", fontSize: 14 }}>
        No data available
      </div>
    );
  }

  const total = data.reduce((sum, item) => sum + (item[dataKey] || 0), 0);

  const formattedData = data.map((item) => ({
    ...item,
    percentage: total > 0 ? ((item[dataKey] / total) * 100).toFixed(0) : "0",
  }));

  if (horizontal) {
    const R_MARGIN = 70;
    const BAR_HEIGHT = 50;
    const chartHeight = Math.max(formattedData.length * BAR_HEIGHT + 40, 200);

    return (
      <div style={{ flex: 1, width: "100%", minHeight: chartHeight }}>
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            data={formattedData}
            layout="vertical"
            margin={{ top: 8, right: R_MARGIN, bottom: 20, left: 0 }}
            barCategoryGap="30%"
          >
            <CartesianGrid
              strokeDasharray="4 4"
              horizontal={false}
              stroke="#e5e5e5"
            />

            <XAxis
              type="number"
              domain={[0, (dataMax) => Math.ceil(dataMax * 1.15) || 12]}
              tick={{ fill: "#667F93", fontSize: 12 }}
              axisLine={false}
              tickLine={false}
            />

            <YAxis
              type="category"
              dataKey={nameKey}
              width={Y_WIDTH}
              tick={<CategoryTick />}
              tickLine={false}
              axisLine={false}
            />

            <Tooltip
              cursor={{ fill: "rgba(0,0,0,0.04)" }}
              content={<CustomTooltip />}
            />

            <Bar dataKey={dataKey} radius={[0, 6, 6, 0]} barSize={28}>
              <LabelList
                content={
                  <HorizontalBarLabel
                    formattedData={formattedData}
                    dataKey={dataKey}
                  />
                }
              />
              {formattedData.map((item) => (
                <Cell key={item[nameKey]} fill={color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  const chartWidth = Math.max(data.length * 110, 600);

  return (
    <div className="bar-scroll-wrapper">
      <div style={{ width: chartWidth, height: "100%" }}>
        <BarChart
          width={chartWidth}
          height={350}
          data={formattedData}
          barCategoryGap="25%"
          margin={{ top: 50, right: 20, left: 20, bottom: 10 }}
        >
          <CartesianGrid
            strokeDasharray="4 4"
            vertical={false}
            stroke="#e5e5e5"
          />

          <XAxis
            dataKey={nameKey}
            interval={0}
            height={60}
            tick={<WrappedCategoryTick />}
          />

          <YAxis hide />

          <Tooltip
            cursor={false}
            content={<CustomTooltip />}
          />

          <Bar dataKey={dataKey} radius={[10, 10, 0, 0]} barSize={35}>
            <LabelList
              dataKey={dataKey}
              position="top"
              content={<VerticalBarLabel formattedData={formattedData} />}
            />
            {formattedData.map((item) => (
              <Cell key={item[nameKey]} fill={color} />
            ))}
          </Bar>
        </BarChart>
      </div>
    </div>
  );
};

const tooltipPayloadShape = PropTypes.arrayOf(
  PropTypes.shape({
    payload: PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      percentage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  }),
);

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: tooltipPayloadShape,
};

CategoryTick.propTypes = {
  y: PropTypes.number,
  payload: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};

HorizontalBarLabel.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  width: PropTypes.number,
  height: PropTypes.number,
  index: PropTypes.number,
  formattedData: PropTypes.arrayOf(PropTypes.object),
  dataKey: PropTypes.string,
};

WrappedCategoryTick.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  payload: PropTypes.shape({
    value: PropTypes.string,
  }),
};

VerticalBarLabel.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  width: PropTypes.number,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  index: PropTypes.number,
  formattedData: PropTypes.arrayOf(PropTypes.object),
};

CommonBarGraph.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  dataKey: PropTypes.string,
  nameKey: PropTypes.string,
  color: PropTypes.string,
  horizontal: PropTypes.bool,
};

export default CommonBarGraph;
